import axios from '../../axios/index.js'
import store from '../../../store/store.js'
import router from '@/router'

// Token Refresh
let isAlreadyFetchingAccessToken = false
let subscribers = []

function onAccessTokenFetched(access_token) {
  subscribers = subscribers.filter(callback => callback(access_token))
}

function addSubscriber(callback) {
  subscribers.push(callback)
}

export default {
  init() {
    axios.interceptors.response.use(
      function(response) {
        return response
      },
      function(error) {
        // const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config

        // if (status === 401) {

        if (
          response &&
          response.status === 401 &&
          !config.url.includes('auth')
        ) {
          if (!isAlreadyFetchingAccessToken) {
            isAlreadyFetchingAccessToken = true
            store
              .dispatch('auth/fetchAccessToken')
              .then(access_token => {
                isAlreadyFetchingAccessToken = false
                onAccessTokenFetched(access_token)
              })
              .catch(error => {
                localStorage.removeItem('accessToken')
                localStorage.removeItem('userInfo')
                router.push('/login').catch(() => {})
              })
          }

          const retryOriginalRequest = new Promise(resolve => {
            addSubscriber(access_token => {
              originalRequest.headers.Authorization = `Bearer ${access_token}`
              resolve(axios(originalRequest))
            })
          })
          return retryOriginalRequest
        } else if (
          config.url.includes('auth') &&
          response.data.hasOwnProperty('error')
        ) {
          return Promise.reject({
            message: response.data.error,
            status: response.data.status,
          })
        }
        return Promise.reject(error)
      },
    )
  },

  updateNetworkDomain(network_id, domain_id, name, set, bac) {
    return axios.patch(`/networks/${network_id}/domains/${domain_id}`, {
      name: name,
      backend_set_id: set.id,
      bac: bac,
    })
  },

  deleteNetworkDomain(network_id, domain_id) {
    return axios.delete(`/networks/${network_id}/domains/${domain_id}`)
  },

  addNetworkDomain(network_id, domain) {
    return axios.post(`/networks/${network_id}/domains`, {
      name: domain.name,
      backend_set_id: domain.set.id,
      bac: domain.bac,
    })
  },

  getNetworks() {
    return axios.get('networks')
  },
  getNetworkDomains(network_id) {
    return axios.get(`/networks/${network_id}/domains`)
  },
  getNetworkFirwallEntries(network_id) {
    return axios.get(`/networks/${network_id}/firewall`)
  },
  createOrUpdateIPFirewallEntry(network_id, cidr, whitelist, id) {
    return axios.post(`/networks/${network_id}/firewall`, {
      cidr: cidr,
      whitelist: whitelist,
      id: id,
    })
  },
  deleteFirewallEntry(network_id, entry_id) {
    return axios.delete(`/networks/${network_id}/firewall/${entry_id}`)
  },

  getNetworkASNEntries(network_id) {
    return axios.get(`/networks/${network_id}/asnFirewall`)
  },
  createOrUpdateNetworkASNEntry(network_id, asn, whitelist, id) {
    return axios.post(`/networks/${network_id}/asnFirewall`, {
      asn: asn,
      whitelist: whitelist,
      id: id,
    })
  },
  deleteNetworkASNEntry(network_id, entry_id) {
    return axios.delete(`/networks/${network_id}/asnFirewall/${entry_id}`)
  },

  getNetworkCountryEntries(network_id) {
    return axios.get(`/networks/${network_id}/countryFirewall`)
  },
  createOrUpdateNetworkCountryEntry(network_id, country_id, whitelist, id) {
    return axios.post(`/networks/${network_id}/countryFirewall`, {
      country_id: country_id,
      whitelist: whitelist,
      id: id,
    })
  },
  deleteNetworkCountryEntry(network_id, entry_id) {
    return axios.delete(`/networks/${network_id}/countryFirewall/${entry_id}`)
  },

  fetchAllCountries() {
    return axios.get(`/countries/fetch-all`)
  },

  getBackendSets(network_id) {
    return axios.get(`/networks/${network_id}/backendSets`)
  },

  getSubUsers(network_id) {
    return axios.get(`/networks/${network_id}/subUsers`)
  },

  createSubUser(network_id, email, perms) {
    return axios.post(`/networks/${network_id}/subUsers`, {
      email: email,
      perms: perms,
    })
  },
  updateSubUser(network_id, user_id, perms) {
    return axios.patch(`/networks/${network_id}/subUsers/${user_id}`, perms)
  },
  deleteSubUser(network_id, user_id) {
    return axios.delete(`/networks/${network_id}/subUsers/${user_id}`)
  },
  getBackendSetInfo(network_id, backend_set_id) {
    return axios.get(`/networks/${network_id}/backendSets/${backend_set_id}`)
  },
  updateBackendSet(network_id, backend_set_id, name, backends, proxy_protocol, vulcan_ac_enabled, load_balancing_mode) {
    return axios.patch(
      `/networks/${network_id}/backendSets/${backend_set_id}`,
      {
        name: name,
        backends: backends,
        proxy_protocol: proxy_protocol,
        vulcan_ac_enabled: vulcan_ac_enabled,
        load_balancing_mode: load_balancing_mode
      },
    )
  },
  deleteBackendSet(network_id, backend_set_id) {
    return axios.delete(`/networks/${network_id}/backendSets/${backend_set_id}`)
  },
  createBackendSet(network_id, name, backends, proxy_protocol, vulcan_ac_enabled, load_balancing_mode) {
    return axios.post(`/networks/${network_id}/backendSets`, {
      name: name,
      backends: backends,
      proxy_protocol: proxy_protocol,
      vulcan_ac_enabled: vulcan_ac_enabled,
      load_balancing_mode: load_balancing_mode
    })
  },
  preVerifyDomain(network_id, name) {
    return axios.post(`/networks/${network_id}/domains/preverify`, {
      domain_name: name,
    })
  },
  verifyDomain(network_id, domain_id) {
    return axios.get(`/networks/${network_id}/domains/${domain_id}/verify`)
  },
  createNetwork(name) {
    return axios.post('networks', {
      name,
    })
  },
  updateNetwork(network_id, networkUpdates) {
    return axios.patch(`/networks/${network_id}`, networkUpdates)
  },
  deleteNetwork(network_id) {
    return axios.delete(`/networks/${network_id}`)
  },
  togglePremiumNetwork(network_id, state) {
    return axios.post(`/networks/${network_id}/toggle-premium`, {
      state,
    })
  },
  getOverview(network_id) {
    return axios.get(`/networks/${network_id}/stats/overview`)
  },
  getPlayersGraph(network_id, points = 360, period = '1d') {
    return axios.get(
      `/networks/${network_id}/stats/players?points=${points}&period=${period}`,
    )
  },
  getBitrateGraph(network_id, points = 360, period = '1d') {
    return axios.get(
      `/networks/${network_id}/stats/bitrate?points=${points}&period=${period}`,
    )
  },
  getConnectionsGraph(network_id, points = 360, period = '1d') {
    return axios.get(
      `/networks/${network_id}/stats/connPerSecond?points=${points}&period=${period}`,
    )
  },
  getConnetionsPerSecondGraph(network_id, points = 360, period = '1d') {
    return axios.get(
      `/networks/${network_id}/stats/connPerSecond?points=${points}&period=${period}`,
    )
  },
  getMitigatedCount(network_id, period = '1d') {
    return axios.get(
      `/networks/${network_id}/analytics/mitigatedCount?period=${period}`,
    )
  },
  getBounceRate(network_id, period = '1d') {
    return axios.get(
      `/networks/${network_id}/analytics/bounceRate?period=${period}`,
    )
  },
  getBandwidth(network_id, period = '1M') {
    return axios.get(`/networks/${network_id}/stats/bandwidth?period=${period}`)
  },
  getBillingBandwidth(network_id, period = '1M') {
    return axios.get(`/networks/${network_id}/stats/billingBandwidth`)
  },
  getBillingCharges(network_id, period = '1M') {
    return axios.get(`/networks/${network_id}/stats/billingCharges`)
  },
  getCountryHeatmap(network_id, period = '7d') {
    return axios.get(`/networks/${network_id}/analytics/countryHeatmap?period=${period}`)
  },
  getTopDomains(network_id, period = '1d') {
    return axios.get(
      `/networks/${network_id}/analytics/topDomains?period=${period}`,
    )
  },
  getUniqueUsers(network_id, period = '1d') {
    return axios.get(
      `/networks/${network_id}/analytics/uniqueUsers?period=${period}`,
    )
  },
  getMCVersionBreakdown(network_id, period = '1d') {
    return axios.get(
      `/networks/${network_id}/analytics/mcVersionBreakdown?period=${period}`,
    )
  },
  getPlayerRetention(network_id) {
    return axios.get(`/networks/${network_id}/analytics/retention`)
  },
  getTunnels(network_id) {
    return axios.get(`/networks/${network_id}/bedrockTunnels`)
  },
  getTunnelLocations(network_id) {
    return axios.get(`/networks/${network_id}/bedrockTunnels/locations`)
  },
  addTunnel(network_id, tunnel) {
    return axios.post(`/networks/${network_id}/bedrockTunnels`, {
      name: tunnel.name,
      backend_set_id: tunnel.set.id,
      location: tunnel.location,
      proxy_protocol: 2,
      // omit when the caller did not decide: the panel leaves an absent
      // field unchanged on update and applies the DB default on create
      ...(typeof tunnel.https_signaling === 'boolean' && {
        https_signaling: tunnel.https_signaling,
      }),
    })
  },
  updateTunnel(network_id, tunnel) {
    return axios.patch(`/networks/${network_id}/bedrockTunnels/${tunnel.id}`, {
      name: tunnel.name,
      backend_set_id: tunnel.set.id,
      proxy_protocol: 2,
      // omit when the caller did not decide: the panel leaves an absent
      // field unchanged on update and applies the DB default on create
      ...(typeof tunnel.https_signaling === 'boolean' && {
        https_signaling: tunnel.https_signaling,
      }),
    })
  },
  deleteTunnel(network_id, entry_id) {
    return axios.delete(`/networks/${network_id}/bedrockTunnels/${entry_id}`)
  },
  getCertificates(network_id) {
    return axios.get(`/networks/${network_id}/bedrockCertificates`)
  },
  getCertificateSuggestions(network_id) {
    return axios.get(`/networks/${network_id}/bedrockCertificates/suggestions`)
  },
  getCertificate(network_id, cert_id) {
    return axios.get(`/networks/${network_id}/bedrockCertificates/${cert_id}`)
  },
  addCertificate(network_id, payload) {
    // payload: {type:'managed', hostname, scope} | {type:'uploaded', certificate, chain, private_key}
    return axios.post(`/networks/${network_id}/bedrockCertificates`, payload)
  },
  deleteCertificate(network_id, cert_id) {
    return axios.delete(`/networks/${network_id}/bedrockCertificates/${cert_id}`)
  },
  checkCertificate(network_id, cert_id) {
    return axios.post(`/networks/${network_id}/bedrockCertificates/${cert_id}/check`)
  },
  getVerificationServer(network_id) {
    return axios.get(`/networks/${network_id}/verificationserver`)
  },
  addVerificationServer(network_id, vs) {
    return axios.post(`/networks/${network_id}/verificationserver`, vs)
  },
  updateVerifcationServer(network_id, vs) {
    return axios.patch(`/networks/${network_id}/verificationserver`, vs)
  },
  deleteVerificationServer(network_id) {
    return axios.delete(`/networks/${network_id}/verificationserver`)
  },
  getListOfAttackHistory(network_id) {
    return axios.get(`/networks/${network_id}/attacks`)
  },
  getListOfAttackDetails({network_id, attackTime}) {
    return axios.get(`/networks/${network_id}/stats/cpsInRange?from=${attackTime.from}&to=${attackTime.to}`)
  },
  getDroppedEventsDetails({network_id, attackTime}) {
    return axios.get(`/networks/${network_id}/stats/mitigationDropInRange?from=${attackTime.from}&to=${attackTime.to}`)
  },
  getWebhookDetails(network_id) {
    return axios.get(`/networks/${network_id}`)
  },
  updateWebhookDetails({network_id, webhookPayload}) {
    return axios.patch(`/networks/${network_id}`, webhookPayload)
  },
}
